import React, { useState, useEffect, useRef } from 'react';
import { Container, Box, Typography, Alert, IconButton, Stack, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { getPlans, getServiceTypes } from '@services/planService';
import { createShowcase, updateShowcase } from '@services/showcaseService';
import './ShowcaseForm.scss';
import { toast } from 'react-toastify';
import { EditorState, ContentState } from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import createHashtagPlugin from '@draft-js-plugins/hashtag';
import '@draft-js-plugins/hashtag/lib/plugin.css';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { CircularProgress } from '@mui/material';


const hashtagPlugin = createHashtagPlugin();
const plugins = [hashtagPlugin];

interface ShowcaseMedia {
  key: string;
  mediaType: string;
}

interface ShowcaseData {
  _id?: string;
  media?: ShowcaseMedia[];
  content?: string;
  planIds?: string[];
  serviceTypeIds?: string[];
  isFromInstagram?: boolean;
  postType?: string;
}

interface Plan {
  _id: string;
  name: string;
}

interface ServiceType {
  _id: string;
  name: string;
  zh_name: string;
}

interface ShowcaseFormData {
  media: ShowcaseMedia[];
  files: File[];
  content: string;
  planIds: string[];
  serviceTypeId: string;
  postType: string;
}

interface SubmitOverrideData extends ShowcaseData {
  files: File[];
  content: string;
  planIds: string[];
  serviceTypeId: string;
}

export interface ShowcaseFormProps {
  selectedStylist: string;
  showcase?: ShowcaseData | null;
  closeModal: () => void;
  isInDialog?: boolean;
  hidePlans?: boolean;
  hidePostType?: boolean;
  onSubmitOverride?: ((data: SubmitOverrideData) => Promise<void>) | null;
}

function ShowcaseForm({ selectedStylist, showcase, closeModal, isInDialog = false, hidePlans = false, hidePostType = false, onSubmitOverride = null }: ShowcaseFormProps) {
  const { t } = useTranslation();
  const { control, handleSubmit: handleFormSubmit, formState: { errors, isSubmitting }, setValue, watch } = useForm<ShowcaseFormData>({
    defaultValues: {
      media: showcase?.media || [],
      files: [],
      content: showcase?.content || '',
      planIds: showcase?.planIds || [],
      serviceTypeId: showcase?.serviceTypeIds?.[0] || '',
      postType: showcase?.postType || 'work'
    }
  });
  const [editorState, setEditorState] = useState(() => {
    if (showcase?.content) {
      const contentState = ContentState.createFromText(showcase.content);
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });
  const [plans, setPlans] = useState<Plan[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [error, setError] = useState('');
  const editorRef = useRef<{ focus: () => void } | null>(null);

  // Watch both media and files
  const media = watch('media');
  const files = watch('files');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Always fetch service types
        const typesResponse = await getServiceTypes();
        setServiceTypes((Array.isArray(typesResponse) ? typesResponse : []) as ServiceType[]);
      } catch (err) {
        console.error('Error fetching service types:', err);
      }

      if (hidePlans) {
        console.log('Plans hidden for quick onboarding context');
        return;
      }

      try {
        const fetchedPlans = await getPlans();
        setPlans(fetchedPlans.data || []);
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError(t('showcaseForm.failedToUploadShowcase'));
      }
    };
    fetchData();
  }, [t, hidePlans]);

  const handleRemoveMedia = (index: number) => {
    try {
      console.log('Removing existing media at index:', index);
      const newMedia = media.filter((_: ShowcaseMedia, i: number) => i !== index);
      setValue('media', newMedia);
    } catch (error) {
      console.error('Error removing media:', error);
      toast.error(t('showcaseForm.mediaRemovalError', 'Error removing media'));
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const newFiles = Array.from(event.target.files || []);
      console.log('Adding new files:', newFiles.length);
      // Accumulate files instead of replacing, with deduplication
      const prevFiles = files || [];
      const existingNames = prevFiles.map((f: File) => f.name);
      const uniqueNewFiles = newFiles.filter(file =>
        !existingNames.includes(file.name)
      );
      console.log('Unique new files:', uniqueNewFiles.length);
      setValue('files', [...prevFiles, ...uniqueNewFiles]);
    } catch (error) {
      console.error('Error handling file selection:', error);
      toast.error(t('showcaseForm.fileSelectionError', 'Error selecting files'));
    } finally {
      // Reset the input to allow selecting the same file again
      event.target.value = '';
    }
  };

  const handleRemoveSelectedFile = (index: number) => {
    try {
      console.log('Removing new file at index:', index);
      const newFiles = files.filter((_: File, i: number) => i !== index);
      setValue('files', newFiles);
    } catch (error) {
      console.error('Error removing file:', error);
      toast.error(t('showcaseForm.fileRemovalError', 'Error removing file'));
    }
  };

  const onSubmit = async (data: ShowcaseFormData) => {
    try {
      const submittingToast = toast.loading(showcase ? t('showcaseForm.saving', 'Saving...') : t('showcaseForm.uploading'));

      const content = editorState.getCurrentContent().getPlainText();

      // Use override if provided (for custom auth like Instagram onboarding token)
      if (onSubmitOverride) {
        await onSubmitOverride({
          ...showcase,
          files: data.files,
          media: data.media,
          content,
          planIds: data.planIds,
          serviceTypeId: data.serviceTypeId,
          postType: data.postType
        });
      } else if (showcase) {
        await updateShowcase({
          _id: showcase!._id!,
          files: data.files,
          media: data.media,
          content,
          planIds: data.planIds.join(','),
          serviceTypeId: data.serviceTypeId,
          postType: data.postType
        });
      } else {
        await createShowcase({
          files: data.files,
          content,
          planIds: data.planIds.join(','),
          stylistId: selectedStylist,
          serviceTypeId: data.serviceTypeId,
          postType: data.postType
        });
      }

      toast.update(submittingToast, {
        render: showcase ? t('showcaseForm.showcaseSaved', 'Showcase saved!') : t('showcaseForm.showcaseUploaded'),
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });

      closeModal();
    } catch (error) {
      console.error('Error uploading showcase:', error);
      const errorMessage = (error as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message || (error as { message?: string }).message || t('showcaseForm.failedToUploadShowcase');
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    isInDialog ? (
      <Dialog open={true} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>{showcase ? t('showcaseManagement.editShowcase') : t('showcaseManagement.createShowcase')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleFormSubmit(onSubmit)}>
            <Stack spacing={3}>
              {!showcase?.isFromInstagram && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('showcaseForm.selectImagesOrVideos')}
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    {t('showcaseForm.selectFiles')}
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*,video/*"
                      onChange={handleFileSelect}
                    />
                  </Button>
                </Box>
              )}

              {/* Enhanced Media Management Section */}
              <Box>
                {(media.length > 0 || files.length > 0) && (
                  <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                    {t('showcaseForm.mediaFiles', 'Media Files')} ({media.length + files.length})
                  </Typography>
                )}

                <Box className="preview-section" sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 2
                }}>
                  {/* Existing Media Files */}
                  {media.map((mediaItem: ShowcaseMedia, index: number) => (
                    <Box
                      key={`existing-${index}`}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: 'success.light',
                        bgcolor: 'success.50'
                      }}
                    >
                      {!showcase?.isFromInstagram && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveMedia(index)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' },
                            zIndex: 1
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}

                      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                        <Chip
                          size="small"
                          label="Existing"
                          color="success"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>

                      {mediaItem.mediaType === 'image' ? (
                        <img
                          src={`${import.meta.env.VITE_R2_ENDPOINT}/${mediaItem.key}`}
                          alt={`Preview ${index}`}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <video
                          src={`${import.meta.env.VITE_R2_ENDPOINT}/${mediaItem.key}`}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover'
                          }}
                        />
                      )}

                      <Box sx={{ p: 1, bgcolor: 'white' }}>
                        <Typography variant="caption" noWrap>
                          {mediaItem.key.split('-').pop()?.substring(0, 20)}...
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  {/* New Files to Upload */}
                  {!showcase?.isFromInstagram && files.map((file: File, index: number) => (
                    <Box
                      key={`new-${index}`}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: 'primary.light',
                        bgcolor: 'primary.50'
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveSelectedFile(index)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' },
                          zIndex: 1
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>

                      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                        <Chip
                          size="small"
                          label="New"
                          color="primary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>

                      {file.type.startsWith('image') ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index}`}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <video
                          src={URL.createObjectURL(file)}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover'
                          }}
                        />
                      )}

                      <Box sx={{ p: 1, bgcolor: 'white' }}>
                        <Typography variant="caption" noWrap>
                          {file.name.substring(0, 20)}{file.name.length > 20 ? '...' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {(media.length === 0 && files.length === 0) && (
                  <Box sx={{
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    bgcolor: 'grey.50'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('showcaseForm.noMediaSelected', 'No media files selected')}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('showcaseForm.content')} <br/>{t('showcaseForm.useHashtags')}
                    </Typography>
                    <Box
                      className="editor"
                      onClick={() => editorRef.current?.focus()}
                      sx={{
                        border: 1,
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        p: 2,
                        minHeight: 200,
                        cursor: 'text'
                      }}
                    >
                      <Editor
                        editorState={editorState}
                        onChange={(state: EditorState) => {
                          setEditorState(state);
                          field.onChange(state.getCurrentContent().getPlainText());
                        }}
                        plugins={plugins}
                        ref={editorRef}
                      />
                    </Box>
                    {errors.content && (
                      <Typography color="error" variant="caption">
                        {errors.content.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />

              {!hidePlans && (
                <Controller
                  name="planIds"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Typography variant="subtitle1" gutterBottom>
                        {t('showcaseForm.selectPlans')}
                      </Typography>
                      <Select
                        {...field}
                        labelId="plan-label"
                        label={t('showcaseForm.selectPlans')}
                        multiple
                        renderValue={(selected: string[]) =>
                          plans
                            .filter((plan: Plan) => selected.includes(plan._id))
                            .map((plan: Plan) => plan.name)
                            .join(', ')
                        }
                      >
                        {plans.map((plan: Plan) => (
                          <MenuItem key={plan._id} value={plan._id}>
                            {plan.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.planIds && (
                        <Typography color="error" variant="caption">
                          {errors.planIds.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              )}

              {serviceTypes.length > 0 && (
                <Controller
                  name="serviceTypeId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="service-type-label">
                        {t('showcaseForm.serviceType', 'Service Type (optional)')}
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="service-type-label"
                        label={t('showcaseForm.serviceType', 'Service Type (optional)')}
                      >
                        <MenuItem value="">
                          <em>{t('showcaseForm.noServiceType', 'None -- AI will classify later')}</em>
                        </MenuItem>
                        {serviceTypes.map((type: ServiceType) => (
                          <MenuItem key={type._id} value={type._id}>
                            {type.name} ({type.zh_name})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}

              {!hidePostType && (
                <Controller
                  name="postType"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        {t('showcaseForm.postType')}
                      </Typography>
                      <ToggleButtonGroup
                        value={field.value}
                        exclusive
                        onChange={(_, val) => val && field.onChange(val)}
                        fullWidth
                        size="small"
                      >
                        <ToggleButton value="work" sx={{ flex: 1 }}>
                          {t('showcaseForm.postTypeShowcase')}
                        </ToggleButton>
                        <ToggleButton value="brochure" sx={{ flex: 1 }}>
                          {t('showcaseForm.postTypeBrochure')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {field.value === 'brochure' && (
                        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                          {t('showcaseForm.postTypeBrochureDesc')}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              )}
            </Stack>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFormSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : (
              showcase ? t('showcaseForm.save', 'Save') : t('showcaseForm.upload')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    ) : (
      <Container className="showcase-upload-page">
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleFormSubmit(onSubmit)}>
          <Stack spacing={3}>
            {!showcase?.isFromInstagram && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {t('showcaseForm.selectImagesOrVideos')}
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {t('showcaseForm.selectFiles')}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                  />
                </Button>
              </Box>
            )}

            {/* Enhanced Media Management Section */}
            <Box>
              {(media.length > 0 || files.length > 0) && (
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                  {t('showcaseForm.mediaFiles', 'Media Files')} ({media.length + files.length})
                </Typography>
              )}

              <Box className="preview-section" sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 2
              }}>
                {/* Existing Media Files */}
                {media.map((mediaItem: ShowcaseMedia, index: number) => (
                  <Box
                    key={`existing-${index}`}
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: 'success.light',
                      bgcolor: 'success.50'
                    }}
                  >
                    {!showcase?.isFromInstagram && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveMedia(index)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' },
                          zIndex: 1
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}

                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                      <Chip
                        size="small"
                        label="Existing"
                        color="success"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>

                    {mediaItem.mediaType === 'image' ? (
                      <img
                        src={`${import.meta.env.VITE_R2_ENDPOINT}/${mediaItem.key}`}
                        alt={`Preview ${index}`}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <video
                        src={`${import.meta.env.VITE_R2_ENDPOINT}/${mediaItem.key}`}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover'
                        }}
                      />
                    )}

                    <Box sx={{ p: 1, bgcolor: 'white' }}>
                      <Typography variant="caption" noWrap>
                        {mediaItem.key.split('-').pop()?.substring(0, 20)}...
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {/* New Files to Upload */}
                {!showcase?.isFromInstagram && files.map((file: File, index: number) => (
                  <Box
                    key={`new-${index}`}
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: 'primary.light',
                      bgcolor: 'primary.50'
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSelectedFile(index)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                        zIndex: 1
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>

                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                      <Chip
                        size="small"
                        label="New"
                        color="primary"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>

                    {file.type.startsWith('image') ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index}`}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <video
                        src={URL.createObjectURL(file)}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover'
                        }}
                      />
                    )}

                    <Box sx={{ p: 1, bgcolor: 'white' }}>
                      <Typography variant="caption" noWrap>
                        {file.name.substring(0, 20)}{file.name.length > 20 ? '...' : ''}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {(media.length === 0 && files.length === 0) && (
                <Box sx={{
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'grey.50'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('showcaseForm.noMediaSelected', 'No media files selected')}
                  </Typography>
                </Box>
              )}
            </Box>

            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('showcaseForm.content')} <br/>{t('showcaseForm.useHashtags')}
                  </Typography>
                  <Box
                    className="editor"
                    onClick={() => editorRef.current?.focus()}
                    sx={{
                      border: 1,
                      borderColor: errors.content ? 'error.main' : 'grey.300',
                      borderRadius: 1,
                      p: 2,
                      minHeight: 200,
                      cursor: 'text'
                    }}
                  >
                    <Editor
                      editorState={editorState}
                      onChange={(state: EditorState) => {
                        setEditorState(state);
                        field.onChange(state.getCurrentContent().getPlainText());
                      }}
                      plugins={plugins}
                      ref={editorRef}
                    />
                  </Box>
                  {errors.content && (
                    <Typography color="error" variant="caption">
                      {errors.content.message}
                    </Typography>
                  )}
                </Box>
              )}
            />

            {!hidePlans && (
              <Controller
                name="planIds"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('showcaseForm.selectPlans')}
                    </Typography>
                    <Select
                      {...field}
                      labelId="plan-label"
                      label={t('showcaseForm.selectPlans')}
                      multiple
                      renderValue={(selected: string[]) =>
                        plans
                          .filter((plan: Plan) => selected.includes(plan._id))
                          .map((plan: Plan) => plan.name)
                          .join(', ')
                      }
                    >
                      {plans.map((plan: Plan) => (
                        <MenuItem key={plan._id} value={plan._id}>
                          {plan.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.planIds && (
                      <Typography color="error" variant="caption">
                        {errors.planIds.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            )}

            {serviceTypes.length > 0 && (
              <Controller
                name="serviceTypeId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="service-type-label-standalone">
                      {t('showcaseForm.serviceType', 'Service Type (optional)')}
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="service-type-label-standalone"
                      label={t('showcaseForm.serviceType', 'Service Type (optional)')}
                    >
                      <MenuItem value="">
                        <em>{t('showcaseForm.noServiceType', 'None -- AI will classify later')}</em>
                      </MenuItem>
                      {serviceTypes.map((type: ServiceType) => (
                        <MenuItem key={type._id} value={type._id}>
                          {type.name} ({type.zh_name})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
            >
              {t('showcaseForm.upload')}
            </Button>
          </Stack>
        </form>
      </Container>
    )
  );
}

export default ShowcaseForm;